import { manifestGroups, type Field } from '../schema';
import { ACTION_INPUTS, ACTION_OUTPUTS, inputSection } from '../content';

const SERVICE_ONLY = new Set(['service', 'loadBalancer', 'autoScaling']);
const SCHEDULED_ONLY = new Set(['tasks']);

/** Manifest reference, generated from the JSON Schema the package ships. */
export function ManifestReference() {
  const groups = manifestGroups();
  const common = groups.filter((g) => !SERVICE_ONLY.has(g.key) && !SCHEDULED_ONLY.has(g.key));
  const service = groups.filter((g) => SERVICE_ONLY.has(g.key));
  const scheduled = groups.filter((g) => SCHEDULED_ONLY.has(g.key));

  return (
    <>
      <section id="manifest">
        <h2>Manifest reference</h2>
        <p className="lede">
          Generated from the JSON Schema shipped in the package, so it describes exactly what the
          installed version enforces. Point your editor at it for inline completion:
        </p>
        <pre className="block">
          <code>
            # yaml-language-server: $schema=https://raw.githubusercontent.com/futuremoney/fargate-deployer/main/schema/manifest.schema.json
          </code>
        </pre>
        {common.map((g) => (
          <FieldTable key={g.key} title={g.key} required={g.required} description={g.description} fields={g.fields} />
        ))}
      </section>

      <section id="manifest-service" className="tight">
        <h3>
          Service <span className="pill">kind: Service</span>
        </h3>
        {service.map((g) => (
          <FieldTable key={g.key} title={g.key} required={g.required} description={g.description} fields={g.fields} />
        ))}
      </section>

      <section id="manifest-scheduled" className="tight">
        <h3>
          Scheduled tasks <span className="pill">kind: ScheduledTasks</span>
        </h3>
        {scheduled.map((g) => (
          <FieldTable key={g.key} title={g.key} required={g.required} description={g.description} fields={g.fields} />
        ))}
      </section>
    </>
  );
}

function FieldTable({
  title,
  required,
  description,
  fields,
}: {
  title: string;
  required: boolean;
  description?: string;
  fields: Field[];
}) {
  // The first row is the block itself; its detail is in the heading.
  const rows = fields.slice(1);

  return (
    <div className="fieldgroup">
      <h4>
        <code>{title}</code>
        {required ? <span className="req">required</span> : <span className="opt">optional</span>}
      </h4>
      {description && <p className="muted small">{description}</p>}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="fields">
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((field) => (
                <tr key={field.path}>
                  <td>
                    <code style={{ marginLeft: `${(field.depth - 1) * 0.9}rem` }}>
                      {field.path.split('.').slice(1).join('.')}
                    </code>
                    {field.required && <span className="req sm">required</span>}
                  </td>
                  <td className="muted">
                    <code>{field.type}</code>
                  </td>
                  <td>
                    {field.description && <div>{field.description}</div>}
                    {field.constraint && <div className="muted small">{field.constraint}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Action inputs and outputs, parsed from the vendored action.yml. */
export function ActionReference() {
  const sections = new Map<string, typeof ACTION_INPUTS>();
  for (const input of ACTION_INPUTS) {
    const key = inputSection(input.name);
    sections.set(key, [...(sections.get(key) ?? []), input]);
  }

  return (
    <>
      <section id="action">
        <h2>Action reference</h2>
        <p className="lede">
          Parsed from <code>action.yml</code>. Only <code>manifest</code> is required — every other
          input has a working default.
        </p>
      </section>

      <section id="action-inputs" className="tight">
        <h3>Inputs</h3>
        {[...sections].map(([title, inputs]) => (
          <div className="fieldgroup" key={title}>
            <h4>{title}</h4>
            <div className="table-wrap">
              <table className="fields">
                <thead>
                  <tr>
                    <th>Input</th>
                    <th>Default</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {inputs.map((input) => (
                    <tr key={input.name}>
                      <td>
                        <code>{input.name}</code>
                        {input.required && <span className="req sm">required</span>}
                      </td>
                      <td className="muted">
                        {input.default ? <code>{input.default}</code> : <span className="small">—</span>}
                      </td>
                      <td>{input.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </section>

      <section id="action-outputs" className="tight">
        <h3>Outputs</h3>
        <div className="table-wrap">
          <table className="fields">
            <thead>
              <tr>
                <th>Output</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {ACTION_OUTPUTS.map((output) => (
                <tr key={output.name}>
                  <td>
                    <code>{output.name}</code>
                  </td>
                  <td>{output.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
